/* eslint-disable consistent-return */
/** adding references
 * https://docs.dnsbelgium.be/be/general/regexp.html
 * https://github.com/mnestorov/regex-patterns?tab=readme-ov-file#austria
 */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import countries from 'i18n-iso-countries';
import ApiClient from '../../loopback/apiClient';
import { EUCountries } from '../../utilities/regions';

export const VATregex = {
  AT: {
    example: 'ATU99999999',
    regex: '^(AT)?U[0-9]{8}$',
  },
  BE: {
    example: 'BE0999999999',
    regex: '^(BE)?[01][0-9]{9}$',
  },
  BG: {
    example: 'BG999999999 ',
    regex: '^(BG)?[0-9]{9,10}$',
  },
  CY: {
    example: 'CY99999999L',
    regex: '^(CY)?[0-9]{8}L$',
  },
  CZ: {
    example: 'CZ99999999 ',
    regex: '^(CZ)?[0-9]{8,10}$',
  },
  DE: {
    example: 'DE999999999',
    regex: '^(DE)?[0-9]{9}$',
  },
  DK: {
    example: 'DK99999999',
    regex: '^(DK)?[0-9]{8}$',
  },
  EE: {
    example: 'EE999999999',
    regex: '^(EE)?[0-9]{9}$',
  },
  GR: {
    example: 'EL999999999',
    regex: '^(EL|GR)?[0-9]{9}$',
  },
  ES: {
    example: 'ESX9999999X',
    regex: '^(ES)?[0-9A-Z][0-9]{7}[0-9A-Z]$',
  },
  FI: {
    example: 'FI99999999',
    regex: '^(FI)?[0-9]{8}$',
  },
  FR: {
    example: 'FRXX999999999',
    regex: '^(FR)?[0-9A-Z]{2}[0-9]{9}$',
  },
  GB: {
    example: 'GB999999999 ',
    regex: '^(GB)?([0-9]{9}([0-9]{3})?|[A-Z]{2}[0-9]{3})$',
  },
  HR: {
    example: 'HR99999999999',
    regex: '^(HR)?[0-9]{11}$',
  },
  HU: {
    example: 'HU99999999',
    regex: '^(HU)?[0-9]{8}$',
  },
  IE: {
    example: 'IE9S99999L/IE1234567W',
    regex: '^(IE)([0-9A-Z*+]{7}[A-Z]{1,2}$)',
  },
  IT: {
    example: 'IT99999999999',
    regex: '^(IT)?[0-9]{11}$',
  },
  LT: {
    example: 'LT999999999',
    regex: '^(LT)?([0-9]{9}|[0-9]{12})$',
  },
  LU: {
    example: 'LU99999999',
    regex: '^(LU)?[0-9]{8}$',
  },
  LV: {
    example: 'LV99999999999',
    regex: '^(LV)?[0-9]{11}$',
  },
  MT: {
    example: 'MT99999999',
    regex: '^(MT)?[0-9]{8}$',
  },
  NL: {
    example: 'NL999999999999',
    regex: '^(NL)?[0-9]{9}B[0-9]{2}$',
  },
  PL: {
    example: 'PL9999999999',
    regex: '^(PL)?[0-9]{10}$',
  },
  PT: {
    example: 'PT999999999',
    regex: '^(PT)?[0-9]{9}$',
  },
  RO: {
    example: 'RO999999999',
    regex: '^(RO)?[0-9]{2,10}$',
  },
  SE: {
    example: 'SE999999999999',
    regex: '^(SE)?[0-9]{12}$',
  },
  SI: {
    example: 'SI99999999',
    regex: '^(SI)?[0-9]{8}$',
  },
  SK: {
    example: 'SK9999999999',
    regex: '^(SK)?[0-9]{10}$',
  },
  SM: {
    example: 'SM99999',
    regex: '^(SM)?[0-9]{5}$',
  },
};

export const isVATRequired = (countryCode) => (countryCode !== 'DE' && EUCountries.includes(countryCode));

export const isVATValid = (value, instituteCountry) => {
  if (!value) {
    return false;
  }
  if (instituteCountry && !!VATregex[instituteCountry]) {
    const countryRegex = VATregex[instituteCountry].regex;
    return value.match(countryRegex);
  }

  // https://docs.payone.com/display/public/PLATFORM/personalid+-+definition
  return value.match('^[0-9a-zA-Z+-./()]{1,32}$');
};

// https://www.abstractapi.com/guides/email-address-pattern-validation <- previous regex, but too advance
export const isValidEmail = (email) => (/^[\w.-]+@[\w.-]+\.\w{2,63}$/.test(email));

// https://docs.payone.com/pages/releaseview.action?pageId=1213939 <- need to follow payone limitation
export const isValidZip = (zip) => (/^[0-9a-zA-Z_./ -]{2,10}$/.test(zip));

export const fieldValidation = async (
  value, type, required, unique, locale, instituteCountryCode,
) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  switch (type) {
    case 'email': // eslint-disable-line no-fallthrough
    {
      const isEmailValid = isValidEmail(value);
      if (!isEmailValid) {
        return <FormattedMessage id="registration.noValidMail" />;
      }
      if (unique) {
        const result = await ApiClient.emailExists(value);
        if (result) {
          return <FormattedMessage id="registration.emailAlreadyExists" />;
        }
      }
      break;
    }
    case 'text':
      return required && value.length === 0 && <FormattedMessage id="registration.noEmptyField" />;
    case 'vat':
    {
      const valid = isVATValid(value, instituteCountryCode);
      if (valid && valid.length) {
        return false;
      }
      let errorOptions = {
        country: countries.getName('AT', locale),
        example: VATregex.AT.example,
      };
      if (instituteCountryCode && !!VATregex[instituteCountryCode]) {
        errorOptions = {
          country: countries.getName(instituteCountryCode, locale),
          example: VATregex[instituteCountryCode].example,
        };
      }
      return <FormattedMessage id="registration.noValidVat" values={errorOptions} />;
    }
    default:
      return true;
  }
};
